import type { SpaceObjectBack, SpaceObjectFront } from '@entities/SpaceObject/type';
import type { SstBack, SstFront } from '@entities/Sst/type';
import { entityAccess } from '@entities/User/permissions';
import { parseUTCDate } from '@root/src/utils/formatters/dates';
import { Alpha2Code } from 'i18n-iso-countries';

export type SstFormatter = (sst: SstBack) => SstFront;
export type SpaceObjectFormatter = (spaceObject: SpaceObjectBack) => SpaceObjectFront;

/**
 * Builds the space object formatter. The object's latest blocks are formatted
 * by `formatSst`, handed in rather than imported: that formatter embeds the
 * object each block is about through this one, and the knot is tied in
 * `src/entities/responseFormatters.ts` so neither module imports the other.
 */
export const createSpaceObjectFromBack =
  (formatSst: SstFormatter): SpaceObjectFormatter =>
  ({
    _id,
    dateOfLaunch,
    createdAt,
    updatedAt,
    accessLevel,
    latestSst,
    expectedEndOfLife,
    operatorsStartDate,
    operatorsEndDate,
    ownersStartDate,
    ownersEndDate,
    ssaStmActorsStartDate,
    ssaStmActorsEndDate,
    name,
    cosparId,
    noradId,
    ituIds,
    nationalId,
    otherNames,
    category,
    orbit,
    meanAltitude,
    beaconFrequency,
    isoCompliance,
    powerSource,
    mass,
    size,
    hardbodyRadius,
    amRatioMin,
    amRatioUsual,
    amRatioMax,
    operators,
    owners,
    pointOfContact,
    pointOfContactDisplay,
    relatedRoles,
    ssaStmActors,
    launchingState,
    ssr,
    additionalInfo,
    isDebris,
    createdBy,
  }) => {
    const accessPermissions = entityAccess(accessLevel);

    return {
      name,
      cosparId,
      noradId,
      ituIds,
      nationalId,
      otherNames,
      category,
      orbit,
      meanAltitude,
      beaconFrequency,
      isoCompliance,
      powerSource,
      mass,
      size,
      hardbodyRadius,
      amRatioMin,
      amRatioUsual,
      amRatioMax,
      operators,
      owners,
      pointOfContact,
      pointOfContactDisplay,
      relatedRoles,
      ssaStmActors,
      launchingState: (() => {
        if (!launchingState) {
          return undefined;
        }
        if (typeof launchingState === 'string') {
          return JSON.parse(launchingState) as Alpha2Code[];
        }
        return launchingState;
      })(),
      ssr,
      additionalInfo,
      isDebris,
      createdBy,
      canEdit: accessPermissions.canEdit,
      canDelete: accessPermissions.canDelete,
      id: _id,
      dateOfLaunch: dateOfLaunch && dateOfLaunch.trim() ? parseUTCDate(dateOfLaunch) : undefined,
      expectedEndOfLife: expectedEndOfLife && expectedEndOfLife.trim() ? parseUTCDate(expectedEndOfLife) : undefined,
      createdAt: createdAt && createdAt.trim() ? parseUTCDate(createdAt) : undefined,
      updatedAt: updatedAt && updatedAt.trim() ? parseUTCDate(updatedAt) : undefined,
      operatorsStartDate:
        operatorsStartDate && operatorsStartDate.trim() ? parseUTCDate(operatorsStartDate) : undefined,
      operatorsEndDate: operatorsEndDate && operatorsEndDate.trim() ? parseUTCDate(operatorsEndDate) : undefined,
      ownersStartDate: ownersStartDate && ownersStartDate.trim() ? parseUTCDate(ownersStartDate) : undefined,
      ownersEndDate: ownersEndDate && ownersEndDate.trim() ? parseUTCDate(ownersEndDate) : undefined,
      ssaStmActorsStartDate:
        ssaStmActorsStartDate && ssaStmActorsStartDate.trim() ? parseUTCDate(ssaStmActorsStartDate) : undefined,
      ssaStmActorsEndDate:
        ssaStmActorsEndDate && ssaStmActorsEndDate.trim() ? parseUTCDate(ssaStmActorsEndDate) : undefined,
      latestSst: latestSst
        ? Object.fromEntries(Object.entries(latestSst).map(([sstType, sst]) => [sstType, formatSst(sst)]))
        : {},
    };
  };
